import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FormatSizeRoundedIcon from "@mui/icons-material/FormatSizeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded";
import EditableImage from "../landingEditor/EditableImage";
import {
  getSegmentVisualStyle,
  updateSegmentVisualStyle,
} from "./segmentVisualStyle";
import {
  getSegmentDefinition,
  SEGMENT_FIELD_TYPES,
} from "./segmentRegistry";

const TAB_INDEX = { content: 0, colors: 1, style: 2, type: 3, image: 4 };
const INDEX_TAB = ["content", "colors", "style", "type", "image"];

const FONT_PRESETS = [
  {
    id: "friendly",
    label: "Cercana",
    heading: "Exo 2",
    body: "Asap",
    url: "https://fonts.googleapis.com/css2?family=Asap:wght@400;500;600;700&family=Exo+2:wght@600;700;800;900&display=swap",
  },
  {
    id: "modern",
    label: "Moderna",
    heading: "Poppins",
    body: "Inter",
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700;800;900&display=swap",
  },
  {
    id: "strong",
    label: "Comercial",
    heading: "Montserrat",
    body: "Open Sans",
    url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Open+Sans:wght@400;500;600;700&display=swap",
  },
  {
    id: "elegant",
    label: "Elegante",
    heading: "Playfair Display",
    body: "Lato",
    url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Playfair+Display:wght@600;700;800;900&display=swap",
  },
];

const COLOR_DEFAULTS = {
  primaryColor: "#F35A3A",
  secondaryColor: "#19242F",
  surfaceColor: "#FFFFFF",
  backgroundColor: "#EEF0F5",
  textColor: "#1A1A2E",
  mutedTextColor: "#5B5D73",
};

const PALETTE_LABELS = [
  "Color 1",
  "Color 2",
  "Color 3",
  "Color 4",
  "Color 5",
];

function paletteFromForm(form) {
  return [
    form?.theme?.primaryColor || COLOR_DEFAULTS.primaryColor,
    form?.theme?.secondaryColor || COLOR_DEFAULTS.secondaryColor,
    form?.theme?.surfaceColor || COLOR_DEFAULTS.surfaceColor,
    form?.theme?.backgroundColor || COLOR_DEFAULTS.backgroundColor,
    form?.theme?.textColor || COLOR_DEFAULTS.textColor,
  ].map(toColorInput);
}

function paletteChanges(sectionId, palette) {
  const [primary, secondary, surface, background, text] = palette.map(toColorInput);
  const globalChanges = {
    "theme.primaryColor": primary,
    "theme.secondaryColor": secondary,
    "theme.surfaceColor": surface,
    "theme.backgroundColor": background,
    "theme.textColor": text,
    "theme.mutedTextColor": text,
  };

  if (["about", "highlights", "contact"].includes(sectionId)) {
    const base = `appearance.${sectionId}`;
    return {
      ...globalChanges,
      [`${base}.backgroundColor`]: background,
      [`${base}.surfaceColor`]: surface,
      [`${base}.accentColor`]: primary,
      [`${base}.titleColor`]: secondary,
      [`${base}.textColor`]: text,
    };
  }

  if (sectionId === "spotlight") {
    return {
      ...globalChanges,
      "catalog.spotlight.primaryColor": primary,
      "catalog.spotlight.secondaryColor": secondary,
    };
  }

  return globalChanges;
}

function hexRgb(color) {
  const value = toColorInput(color).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

function relativeLuminance(color) {
  const channels = hexRgb(color).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(first, second) {
  const light = Math.max(relativeLuminance(first), relativeLuminance(second));
  const dark = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function palettePermutations(values) {
  if (values.length <= 1) return [values];
  return values.flatMap((value, index) =>
    palettePermutations(values.filter((_, current) => current !== index)).map((rest) => [value, ...rest])
  );
}

function randomizedReadablePalette(palette) {
  const values = palette.map(toColorInput);
  const readable = palettePermutations(values).filter((candidate) =>
    contrastRatio(candidate[3], candidate[4]) >= 3 &&
    contrastRatio(candidate[2], candidate[4]) >= 3
  );
  const options = readable.length ? readable : palettePermutations(values);
  if (options.length <= 1) return values;

  const currentKey = values.join("|");
  const alternatives = options.filter((candidate) => candidate.join("|") !== currentKey);
  const candidates = alternatives.length ? alternatives : options;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const INSPECTOR_COLLECTION_IMAGE_LIMIT = 6;
const EMPTY_SEGMENT_PROPERTIES = Object.freeze([]);

function getAtPath(source, path, fallback = "") {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined || value === null ? fallback : value;
}

function registeredFieldValue(form, field, fallback = "") {
  const registeredFallback = field?.fallbackPath
    ? getAtPath(form, field.fallbackPath, field.defaultValue ?? fallback)
    : field?.defaultValue ?? fallback;
  return getAtPath(form, field?.path, registeredFallback);
}

function isSupportedColor(value) {
  const candidate = String(value || "").trim();
  if (!candidate) return true;
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", candidate);
  }
  return /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|var\(--[^)]+\)|[a-z]+)$/i.test(candidate);
}

function registryFieldError(field, value) {
  if (!field) return "";
  const text = String(value ?? "");
  if (Number.isFinite(field.maxLength) && text.length > field.maxLength) {
    return `Máximo ${field.maxLength} caracteres.`;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR && !isSupportedColor(text)) {
    return "Ingresá un color CSS válido, por ejemplo #F35A3A.";
  }
  if (
    field.type === SEGMENT_FIELD_TYPES.SELECT &&
    Array.isArray(field.options) &&
    field.options.length &&
    !field.options.some((option) => option.value === value)
  ) {
    return "Elegí una opción válida.";
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER && text !== "") {
    const number = Number(value);
    if (!Number.isFinite(number)) return "Ingresá un número válido.";
    if (Number.isFinite(field.min) && number < field.min) return `El mínimo es ${field.min}.`;
    if (Number.isFinite(field.max) && number > field.max) return `El máximo es ${field.max}.`;
  }
  return "";
}

function levelOptionsFromRegistry(visualField, key, labels) {
  const limits = visualField?.limits?.[key] || { min: 1, max: 3, step: 1 };
  const min = Number.isFinite(limits.min) ? limits.min : 1;
  const max = Number.isFinite(limits.max) ? limits.max : 3;
  const step = Number.isFinite(limits.step) && limits.step > 0 ? limits.step : 1;
  const options = [];
  for (let value = min; value <= max; value += step) {
    options.push({ value, label: labels[value] || String(value) });
  }
  return options;
}

function normalizeVisualPatch(visualField, patch) {
  return Object.fromEntries(Object.entries(patch).map(([key, rawValue]) => {
    const limits = visualField?.limits?.[key];
    if (!limits) return [key, rawValue];
    const min = Number.isFinite(limits.min) ? limits.min : rawValue;
    const max = Number.isFinite(limits.max) ? limits.max : rawValue;
    const value = Math.max(min, Math.min(max, Number(rawValue)));
    return [key, value];
  }));
}

function toColorInput(value) {
  const candidate = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(candidate)) return candidate;
  if (/^#[0-9a-fA-F]{3}$/.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => char + char).join("")}`;
  }
  const rgb = candidate.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return `#${rgb.slice(1, 4).map((part) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, "0")).join("")}`;
  return "#000000";
}

function directImagePositionField(properties, imageField) {
  const candidates = [];
  if (imageField.path.endsWith("imageUrl")) {
    candidates.push(imageField.path.replace(/imageUrl$/, "imagePosition"));
  }
  if (imageField.path.endsWith("backgroundImageUrl")) {
    candidates.push(imageField.path.replace(/backgroundImageUrl$/, "backgroundImagePosition"));
  }
  return properties.find((property) =>
    property.type === SEGMENT_FIELD_TYPES.SELECT && candidates.includes(property.path)
  );
}

function imageAssetKey(path) {
  return String(path || "image")
    .replace(/\.\d+\./g, "-")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

function imageFieldsFromRegistry(definition, form) {
  const properties = definition?.properties || [];
  const directFields = properties
    .filter((property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.IMAGE)
    .map((property) => {
      const positionField = directImagePositionField(properties, property);
      return {
        ...property,
        key: imageAssetKey(property.path),
        positionPath: positionField?.path,
        positionLabel: positionField?.label,
        positionOptions: positionField?.options || [],
        positionDefaultValue: positionField?.defaultValue,
      };
    });

  const collectionFields = properties.flatMap((property) => {
    if (property.editable === false || property.type !== SEGMENT_FIELD_TYPES.COLLECTION) return [];
    const items = getAtPath(form, property.path, []);
    if (!Array.isArray(items) || !items.length) return [];
    const itemFields = Array.isArray(property.item?.fields) ? property.item.fields : [];
    const images = itemFields.filter((itemField) => itemField.type === SEGMENT_FIELD_TYPES.IMAGE);
    if (!images.length) return [];
    const positionField = itemFields.find((itemField) =>
      itemField.type === SEGMENT_FIELD_TYPES.SELECT && /position/i.test(itemField.key || "")
    );
    const registeredMax = Number.isFinite(property.limits?.maxItems)
      ? property.limits.maxItems
      : items.length;
    const visibleItems = items.slice(0, Math.min(registeredMax, INSPECTOR_COLLECTION_IMAGE_LIMIT));

    return visibleItems.flatMap((item, index) => images.map((imageField) => {
      const path = `${property.path}.${index}.${imageField.key}`;
      return {
        ...imageField,
        path,
        label: item?.name || item?.title || item?.label || `${property.label || "Imagen"} ${index + 1}`,
        key: imageAssetKey(path),
        positionPath: positionField ? `${property.path}.${index}.${positionField.key}` : undefined,
        positionLabel: positionField?.label || "Posición de la imagen",
        positionOptions: positionField?.options || [],
        collectionPath: property.path,
      };
    }));
  });

  const collectionRootsWithImages = new Set(
    collectionFields
      .filter((field) => Boolean(getAtPath(form, field.path, "")))
      .map((field) => String(field.collectionPath || "").split(".")[0])
  );
  const filteredDirectFields = directFields.filter((field) => {
    const root = String(field.path || "").split(".")[0];
    return !collectionRootsWithImages.has(root);
  });

  return [...filteredDirectFields, ...collectionFields];
}

function styleFieldsFromRegistry(definition, imageFields) {
  const imagePositionPaths = new Set(imageFields.map((field) => field.positionPath).filter(Boolean));
  return (definition?.properties || []).filter((property) =>
    property.editable !== false &&
    property.type === SEGMENT_FIELD_TYPES.SELECT &&
    !imagePositionPaths.has(property.path) &&
    /(\.style|\.layout)$/.test(property.path)
  );
}

function LevelSelector({ label, value, options, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        onChange={(_, next) => next && onChange(next)}
      >
        {options.map((option) => (
          <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none", fontWeight: 850 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

export default function LandingSegmentInspector({
  open,
  sectionId,
  initialTool = "content",
  form,
  productsEnabled,
  saving,
  onClose,
  onCommit,
  onCommitMany,
  onOpenTexts,
  onUploadImage,
  onRemoveImage,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const [tab, setTab] = React.useState(TAB_INDEX[initialTool] || 0);
  const [colorDraft, setColorDraft] = React.useState({});
  const [paletteDraft, setPaletteDraft] = React.useState(() => paletteFromForm(form));
  const definition = React.useMemo(() => getSegmentDefinition(sectionId), [sectionId]);
  const registeredProperties = definition?.properties || EMPTY_SEGMENT_PROPERTIES;
  const label = definition?.label || "Sección";
  const visualField = registeredProperties.find(
    (property) => property.type === SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE
  );
  const visual = getSegmentVisualStyle(form?.theme, sectionId);
  const colorFields = React.useMemo(
    () => registeredProperties.filter(
      (property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.COLOR
    ),
    [registeredProperties]
  );
  const imageFields = React.useMemo(
    () => imageFieldsFromRegistry(definition, form),
    [definition, form]
  );
  const styleFields = React.useMemo(
    () => styleFieldsFromRegistry(definition, imageFields),
    [definition, imageFields]
  );
  const visibilityPath = definition?.visibilityPath;
  const visibilityField = registeredProperties.find(
    (property) => property.path === visibilityPath && property.type === SEGMENT_FIELD_TYPES.BOOLEAN
  );
  const overlayField = registeredProperties.find((property) => property.path === "hero.overlay");
  const moduleUnavailable = Boolean(definition?.requiresProducts && !productsEnabled);
  const visible = visibilityPath
    ? getAtPath(form, visibilityPath, definition?.defaultVisible ?? true) !== false
    : true;
  const paletteValidationField = React.useMemo(() => ({
    type: SEGMENT_FIELD_TYPES.COLOR,
    maxLength: Math.max(18, ...colorFields.map((field) => field.maxLength || 0)),
  }), [colorFields]);
  const colorErrors = React.useMemo(
    () => Object.fromEntries(
      colorFields.map((field) => [field.path, registryFieldError(field, colorDraft[field.path])])
    ),
    [colorDraft, colorFields]
  );
  const paletteErrors = React.useMemo(
    () => paletteDraft.map((value) => registryFieldError(paletteValidationField, value)),
    [paletteDraft, paletteValidationField]
  );
  const hasColorErrors = Object.values(colorErrors).some(Boolean) || paletteErrors.some(Boolean);
  const hasSharedColors = colorFields.some(
    (field) => field.shared || String(field.path || "").startsWith("theme.")
  );
  const spacingOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "spacing", {
      1: "Compacto",
      2: "Equilibrado",
      3: "Amplio",
    }),
    [visualField]
  );
  const titleSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "titleSize", {
      1: "Pequeño",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );
  const textStyleOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "textStyle", {
      1: "Suave",
      2: "Firme",
      3: "Impactante",
    }),
    [visualField]
  );
  const imageSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "imageSize", {
      1: "Pequeña",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );

  React.useEffect(() => {
    if (open) setTab(TAB_INDEX[initialTool] || 0);
  }, [initialTool, open, sectionId]);

  React.useEffect(() => {
    const next = {};
    colorFields.forEach((field) => {
      const colorRole = String(field.path || "").split(".").pop();
      const fallbackPath = field.fallbackPath || `theme.${colorRole}`;
      const fallback = getAtPath(form, fallbackPath, field.defaultValue || "#000000");
      next[field.path] = registeredFieldValue(form, field, fallback) || fallback;
    });
    setColorDraft(next);
    setPaletteDraft(paletteFromForm(form));
  }, [colorFields, form, sectionId]);

  const updateVisual = (patch) => {
    if (!visualField) return;
    const normalizedPatch = normalizeVisualPatch(visualField, patch);
    const template = updateSegmentVisualStyle(form?.theme?.template, sectionId, normalizedPatch);
    onCommit("theme.template", template);
  };

  const setPaletteColor = (index, value) => {
    setPaletteDraft((previous) => {
      const next = [...previous];
      next[index] = value;
      setColorDraft((current) => ({
        ...current,
        ...paletteChanges(sectionId, next),
      }));
      return next;
    });
  };

  const applyColors = () => {
    if (hasColorErrors) return;
    onCommitMany({
      ...paletteChanges(sectionId, paletteDraft),
      ...colorDraft,
    });
  };

  const randomizeColors = () => {
    if (paletteErrors.some(Boolean)) return;
    const randomized = randomizedReadablePalette(paletteDraft);
    const changes = paletteChanges(sectionId, randomized);
    setPaletteDraft(randomized);
    setColorDraft((current) => ({ ...current, ...changes }));
    onCommitMany(changes);
  };

  const resetSectionColors = () => {
    const changes = Object.fromEntries(
      colorFields.map((field) => [field.path, field.defaultValue ?? ""])
    );
    if (Object.keys(changes).length) onCommitMany(changes);
  };

  const setVisible = (next) => {
    if (!visibilityPath || moduleUnavailable) return;
    const changes = { [visibilityPath]: next };
    registeredProperties
      .filter((property) =>
        property.type === SEGMENT_FIELD_TYPES.BOOLEAN && /\.enabled$/.test(property.path || "")
      )
      .forEach((property) => {
        changes[property.path] = next;
      });
    if (definition?.requiresProducts && next) changes["sections.showProducts"] = true;
    if (Object.keys(changes).length === 1) onCommit(visibilityPath, next);
    else onCommitMany(changes);
  };

  const drawerPaper = mobile
    ? { borderRadius: "20px 20px 0 0", maxHeight: "88vh" }
    : { width: 410, maxWidth: "92vw" };

  return (
    <Drawer
      anchor={mobile ? "bottom" : "right"}
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: drawerPaper }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: mobile ? "auto" : "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 2, pt: 1.7, pb: 1.1 }}>
          <Box>
            <Typography variant="overline" sx={{ color: "#F35A3A", fontWeight: 950, letterSpacing: 1 }}>
              EDITAR SEGMENTO
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F", lineHeight: 1.1 }}>
              {label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar opciones">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ px: 1, minHeight: 44, borderBottom: "1px solid rgba(25,36,47,.09)" }}
        >
          <Tab icon={<TextFieldsRoundedIcon />} iconPosition="start" label="Textos" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<PaletteRoundedIcon />} iconPosition="start" label="Colores" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<TuneRoundedIcon />} iconPosition="start" label="Estilo" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<FormatSizeRoundedIcon />} iconPosition="start" label="Letra" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
          <Tab icon={<ImageRoundedIcon />} iconPosition="start" label="Imagen" sx={{ minHeight: 44, textTransform: "none", fontWeight: 850 }} />
        </Tabs>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1, bgcolor: "#F7F8FA" }}>
          {INDEX_TAB[tab] === "content" ? (
            <Stack spacing={1.5}>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Los textos, botones y colecciones siguen usando su editor especializado. Acá se reutilizan solamente los controles registrados para el segmento.
              </Alert>
              {!definition ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento todavía no tiene una definición registrada.
                </Alert>
              ) : null}
              {moduleUnavailable ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento requiere el módulo de productos o servicios para poder mostrarse.
                </Alert>
              ) : null}
              <Button
                variant="contained"
                size="large"
                startIcon={<TextFieldsRoundedIcon />}
                onClick={() => onOpenTexts(sectionId)}
                disabled={saving || moduleUnavailable || !definition}
                sx={{ borderRadius: 3, py: 1.35, bgcolor: "#19242F", textTransform: "none", fontWeight: 950 }}
              >
                Cambiar textos y botones
              </Button>
              {visibilityPath ? (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={visible}
                      disabled={saving || moduleUnavailable}
                      onChange={(event) => setVisible(event.target.checked)}
                    />
                  )}
                  label={visible ? "Esta sección está visible" : "Esta sección está oculta"}
                  title={visibilityField?.label || "Visibilidad del segmento"}
                  sx={{ m: 0, p: 1.2, borderRadius: 3, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.09)" }}
                />
              ) : null}
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "colors" ? (
            <Stack spacing={1.5}>
              <Box sx={{ p: 1.35, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                  Paleta de colores
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                  Pegá los cinco colores que te pasó tu diseñador. Después podés probar distintas combinaciones sin perder la paleta.
                </Typography>

                <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 1 }}>
                  {PALETTE_LABELS.map((paletteLabel, index) => (
                    <Stack
                      key={paletteLabel}
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                      sx={{ p: 0.8, borderRadius: 2.5, bgcolor: "#F7F8FA", border: "1px solid rgba(25,36,47,.08)" }}
                    >
                      <Box
                        component="input"
                        type="color"
                        aria-label={paletteLabel}
                        value={toColorInput(paletteDraft[index])}
                        onChange={(event) => setPaletteColor(index, event.target.value)}
                        sx={{ width: 40, height: 38, p: 0.2, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer", flexShrink: 0 }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label={paletteLabel}
                        value={paletteDraft[index] || ""}
                        onChange={(event) => setPaletteColor(index, event.target.value)}
                        inputProps={{ maxLength: paletteValidationField.maxLength }}
                        error={Boolean(paletteErrors[index])}
                        helperText={paletteErrors[index] || " "}
                      />
                    </Stack>
                  ))}
                </Box>
              </Box>

              {hasSharedColors ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Estos colores son generales y pueden repetirse en otros segmentos para mantener una identidad consistente.
                </Alert>
              ) : null}
              {colorFields.map((field) => (
                <Stack key={field.path} direction="row" spacing={1} alignItems="center" sx={{ p: 1.1, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                  <Box
                    component="input"
                    type="color"
                    aria-label={field.label}
                    value={toColorInput(colorDraft[field.path])}
                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                    sx={{ width: 46, height: 42, p: 0.3, border: "1px solid rgba(25,36,47,.15)", borderRadius: 2, bgcolor: "#fff", cursor: "pointer" }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={field.label}
                    value={colorDraft[field.path] || ""}
                    onChange={(event) => setColorDraft((previous) => ({ ...previous, [field.path]: event.target.value }))}
                    inputProps={{ maxLength: field.maxLength }}
                    error={Boolean(colorErrors[field.path])}
                    helperText={colorErrors[field.path] || " "}
                  />
                </Stack>
              ))}
              {hasColorErrors ? (
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  Corregí los colores marcados antes de aplicar los cambios.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={resetSectionColors} disabled={saving || !colorFields.length} sx={{ borderRadius: 3, textTransform: "none", fontWeight: 900 }}>
                  Restablecer
                </Button>
                <Button fullWidth variant="contained" onClick={applyColors} disabled={saving || hasColorErrors} sx={{ borderRadius: 3, bgcolor: "#F35A3A", textTransform: "none", fontWeight: 950 }}>
                  Aplicar colores
                </Button>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<ShuffleRoundedIcon />}
                onClick={randomizeColors}
                disabled={saving || paletteErrors.some(Boolean)}
                sx={{
                  borderRadius: 3,
                  py: 1.25,
                  bgcolor: "#19242F",
                  textTransform: "none",
                  fontWeight: 950,
                  "&:hover": { bgcolor: "#101820" },
                }}
              >
                Aleatorizar colores
              </Button>
              <Typography variant="caption" sx={{ textAlign: "center", color: "rgba(25,36,47,.58)" }}>
                Cada clic redistribuye los mismos cinco tonos y prioriza una combinación legible.
              </Typography>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "style" ? (
            <Stack spacing={2}>
              {visualField ? (
                <LevelSelector
                  label="Aire y separación del segmento"
                  value={visual.spacing}
                  options={spacingOptions}
                  onChange={(spacing) => updateVisual({ spacing })}
                />
              ) : null}

              {styleFields.map((field) => {
                const value = registeredFieldValue(form, field, field.defaultValue || "");
                const error = registryFieldError(field, value);
                return (
                  <FormControl key={field.path} fullWidth size="small" error={Boolean(error)}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={value}
                      disabled={saving}
                      onChange={(event) => {
                        if (!registryFieldError(field, event.target.value)) {
                          onCommit(field.path, event.target.value);
                        }
                      }}
                    >
                      {(field.options || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {error ? <FormHelperText>{error}</FormHelperText> : null}
                  </FormControl>
                );
              })}

              {overlayField ? (
                <LevelSelector
                  label="Oscuridad sobre la imagen"
                  value={String(getAtPath(form, overlayField.path, "")).includes(".82") ? 3 : String(getAtPath(form, overlayField.path, "")).includes(".48") ? 1 : 2}
                  options={[{ value: 1, label: "Suave" }, { value: 2, label: "Legible" }, { value: 3, label: "Intensa" }]}
                  onChange={(value) => onCommit(
                    overlayField.path,
                    value === 1
                      ? "linear-gradient(to bottom, rgba(0,0,0,.08), rgba(8,8,20,.48))"
                      : value === 3
                        ? "linear-gradient(to bottom, rgba(0,0,0,.28), rgba(8,8,20,.82))"
                        : "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))"
                  )}
                />
              ) : null}

              <Box>
                <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>Redondez general de tarjetas y botones</Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={Number(form?.theme?.borderRadius || 10) <= 5 ? 1 : Number(form?.theme?.borderRadius || 10) >= 20 ? 3 : 2}
                  onChange={(_, next) => next && onCommit("theme.borderRadius", next === 1 ? 4 : next === 3 ? 24 : 10)}
                >
                  <ToggleButton value={1}>Recto</ToggleButton>
                  <ToggleButton value={2}>Suave</ToggleButton>
                  <ToggleButton value={3}>Redondo</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "type" ? (
            <Stack spacing={2}>
              {visualField ? (
                <>
                  <LevelSelector
                    label="Tamaño de los títulos de este segmento"
                    value={visual.titleSize}
                    options={titleSizeOptions}
                    onChange={(titleSize) => updateVisual({ titleSize })}
                  />
                  <LevelSelector
                    label="Fuerza visual de los títulos"
                    value={visual.textStyle}
                    options={textStyleOptions}
                    onChange={(textStyle) => updateVisual({ textStyle })}
                  />
                  <Divider />
                </>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Este segmento no declara ajustes tipográficos propios en el registro.
                </Alert>
              )}
              <Typography variant="body2" sx={{ fontWeight: 900 }}>Familia tipográfica de toda la página</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                {FONT_PRESETS.map((preset) => {
                  const selected = form?.theme?.headingFontFamily === preset.heading && form?.theme?.bodyFontFamily === preset.body;
                  return (
                    <Button
                      key={preset.id}
                      variant={selected ? "contained" : "outlined"}
                      onClick={() => onCommitMany({
                        "theme.fontFamily": preset.body,
                        "theme.headingFontFamily": preset.heading,
                        "theme.bodyFontFamily": preset.body,
                        "theme.googleFontsUrl": preset.url,
                      })}
                      sx={{ minHeight: 74, borderRadius: 3, textTransform: "none", fontWeight: 900, bgcolor: selected ? "#19242F" : "#fff" }}
                    >
                      <Stack>
                        <Typography sx={{ fontFamily: preset.heading, fontWeight: 900 }}>{preset.label}</Typography>
                        <Typography variant="caption" sx={{ opacity: .72 }}>{preset.heading} + {preset.body}</Typography>
                      </Stack>
                    </Button>
                  );
                })}
              </Box>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                El tamaño se aplica solamente a este segmento. La familia tipográfica se mantiene global para que la página no parezca armada con partes distintas.
              </Alert>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "image" ? (
            <Stack spacing={1.6}>
              {visualField ? (
                <LevelSelector
                  label="Tamaño visual de las imágenes de este segmento"
                  value={visual.imageSize}
                  options={imageSizeOptions}
                  onChange={(imageSize) => updateVisual({ imageSize })}
                />
              ) : null}

              {imageFields.length ? imageFields.map((field) => (
                <Box key={field.path} sx={{ p: 1.2, borderRadius: 3, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.09)" }}>
                  <EditableImage
                    value={getAtPath(form, field.path, "")}
                    label={field.label}
                    helperText="Podés reemplazarla o eliminarla sin salir del segmento."
                    height={150}
                    disabled={saving || moduleUnavailable}
                    onUpload={(file, options) => onUploadImage(field.path, file, { sectionKey: sectionId, assetKey: field.key }, options)}
                    onRemove={() => onRemoveImage(field.path)}
                  />
                  {field.positionPath && field.positionOptions?.length ? (() => {
                    const value = getAtPath(
                      form,
                      field.positionPath,
                      field.positionDefaultValue || field.positionOptions[0]?.value || "center"
                    );
                    const positionDefinition = {
                      type: SEGMENT_FIELD_TYPES.SELECT,
                      options: field.positionOptions,
                    };
                    const error = registryFieldError(positionDefinition, value);
                    return (
                      <FormControl fullWidth size="small" error={Boolean(error)} sx={{ mt: 1.2 }}>
                        <InputLabel>{field.positionLabel || "Posición de la imagen"}</InputLabel>
                        <Select
                          label={field.positionLabel || "Posición de la imagen"}
                          value={value}
                          disabled={saving || moduleUnavailable}
                          onChange={(event) => {
                            if (!registryFieldError(positionDefinition, event.target.value)) {
                              onCommit(field.positionPath, event.target.value);
                            }
                          }}
                        >
                          {field.positionOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error ? <FormHelperText>{error}</FormHelperText> : null}
                      </FormControl>
                    );
                  })() : null}
                </Box>
              )) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Este segmento usa contenido automático y no tiene una imagen de fondo propia. Podés modificar su tamaño visual y sus colores sin alterar los productos reales.
                </Alert>
              )}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
